import * as React from 'react';
import { Builder, withChildren } from '@builder.io/react';
import image from '@/assets/images/cms/modal.svg?inline';

export interface ModalComponentProps<TSize, TButtonVariant, TButtonSize> {
	readonly children?: React.ReactNode;
	readonly modalSize: TSize;
	readonly buttonText?: string;
	readonly buttonVariant?: TButtonVariant;
	readonly buttonSize?: TButtonSize;
}

const register = <
	TProps extends ModalComponentProps<TSizes[number], TButtonVariants[number], TButtonSizes[number]>,
	TSizes extends readonly string[],
	TButtonVariants extends readonly string[],
	TButtonSizes extends readonly string[],
>(
	Component: React.FC<TProps>,
	options: {
		readonly sizes: TSizes;
		readonly buttonVariants: TButtonVariants;
		readonly buttonSizes: TButtonSizes;
		readonly defaultSize?: TSizes[number];
		readonly defaultButtonVariant?: TButtonVariants[number];
		readonly defaultButtonSize?: TButtonSizes[number];
	},
): void => {
	const ModalWithChildren = withChildren(Component);

	Builder.registerComponent(ModalWithChildren, {
		name: 'Modal',
		image,
		noWrap: true,
		inputs: [
			{
				type: 'string',
				name: 'modalSize',
				required: true,
				enum: Array.from(options.sizes),
				defaultValue: options.defaultSize,
			},
			{
				type: 'string',
				name: 'buttonText',
				required: true,
				defaultValue: 'Launch modal!',
			},
			{
				type: 'string',
				name: 'buttonVariant',
				required: true,
				enum: Array.from(options.buttonVariants),
				defaultValue: options.defaultButtonVariant,
			},
			{
				type: 'string',
				name: 'buttonSize',
				required: true,
				enum: Array.from(options.buttonSizes),
				defaultValue: options.defaultButtonSize,
			},
		],

		defaultChildren: [
			{
				'@type': '@builder.io/sdk:Element',
				component: {
					name: 'Core:Section',
					options: {
						size: 'md',
					},
				},
				responsiveStyles: {
					large: {
						paddingTop: '24px',
						paddingBottom: '24px',
					},
				},
				children: [
					{
						'@type': '@builder.io/sdk:Element',
						component: {
							name: 'Text',
							options: {
								text: `
							<h2>Example modal content</h2>
							<br>
							<p>
								<strong>
									<em>You can edit this content or even delete it and insert your own instead.</em>
								</strong>
							</p>
							<br>
							<p>
								Lorem ipsum dolor sit amet, consectetur adipiscing elit. Vivamus aliquam euismod nisl,
								quis interdum turpis. Proin lacinia erat vitae nunc viverra pretium. Proin pharetra ultrices
								purus, ac luctus metus vehicula non. Nam eget quam nisl. Etiam efficitur rutrum nisi, nec
								tincidunt orci dignissim ut. Curabitur purus est, euismod eget maximus sed, tincidunt ut
								lacus. Vestibulum ante ipsum primis in faucibus orci luctus et ultrices posuere cubilia
								curae; Fusce congue viverra eros, vel molestie elit posuere eget. Phasellus ut ultrices dui.
								In aliquet metus nec tortor fermentum, eu bibendum lorem dignissim. Pellentesque convallis
								ornare dui ut venenatis. Integer cursus ornare erat sit amet ornare.
							</p>
							`,
							},
						},
					},
					{
						'@type': '@builder.io/sdk:Element',
						component: {
							name: 'Core:Button',
							options: {
								text: 'Click me!',
								openInNewTab: false,
								variant: options.defaultButtonVariant,
								size: options.defaultButtonSize,
							},
						},
					},
				],
			},
		],
	});
};

export default register;
